import React, { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import Swal from 'sweetalert2';
import paymentportalservice from '../../../services/paymentportalservice';
import { parseStoredAmount } from './campaignFieldUtils';

// "" means "leave this field alone" — excluded from the request entirely
// rather than sent as a 0 delta. Any other value is parsed as-is, negative
// included: a negative delta is exactly how a decrease is expressed.
const toDelta = (value) => (value === '' ? null : Number(value));

const formatRupees = (value) => `₹${value.toLocaleString('en-IN')}`;

const EditBudgetLimitDialog = React.memo(function EditBudgetLimitDialog({ open, campaign, onClose, onSaved }) {
    const [name, setName] = useState('');
    const [addBudget, setAddBudget] = useState('');
    const [addLimit, setAddLimit] = useState('');
    const [saving, setSaving] = useState(false);

    // Name is a direct edit of the campaign's current value, so — unlike
    // addBudget/addLimit, which are deltas and always reset to blank — it's
    // re-seeded from the campaign each time the dialog (re)opens, same as
    // AddRevenueDialog's amount field.
    useEffect(() => {
        if (!open) return;
        setName(campaign?.name || '');
        setAddBudget('');
        setAddLimit('');
    }, [open, campaign]);

    const currentBudget = parseStoredAmount(campaign?.totalPayment);
    const currentLimit = parseStoredAmount(campaign?.paymentLimit);

    const budgetDelta = toDelta(addBudget);
    const limitDelta = toDelta(addLimit);
    const budgetTouched = budgetDelta != null;
    const limitTouched = limitDelta != null;

    const budgetFinite = !budgetTouched || Number.isFinite(budgetDelta);
    const limitFinite = !limitTouched || Number.isFinite(limitDelta);
    const newBudget = budgetTouched && budgetFinite ? currentBudget + budgetDelta : currentBudget;
    const newLimit = limitTouched && limitFinite ? currentLimit + limitDelta : currentLimit;

    // Mirrors the backend's applyBudgetLimitDeltas exactly
    // (influencer_backend_node routes/campaign.js) — each field's own ₹0
    // floor only applies when that field is actually being touched (an
    // untouched field's already-stored value — e.g. some payment-link
    // routes can already drift paymentLimit negative — is never newly
    // re-validated just because the other field changed); the "already
    // committed" floor only applies when Budget itself changes (Budget
    // minus Limit is exactly how much has already been committed, since
    // every payment-link-creation route decrements Limit and never touches
    // Budget); and the ceiling compares the gap before vs. after rather
    // than an absolute newLimit > newBudget, so a pre-existing drift this
    // edit doesn't worsen is never blocked.
    const budgetBelowZero = budgetTouched && budgetFinite && newBudget < 0;
    const limitBelowZero = limitTouched && limitFinite && newLimit < 0;
    const alreadyCommitted = currentBudget - currentLimit;
    const belowCommitted = budgetTouched && budgetFinite && newBudget < alreadyCommitted;
    const gapBefore = currentLimit - currentBudget;
    const gapAfter = newLimit - newBudget;
    const limitExceedsBudget = budgetFinite && limitFinite && gapAfter > 0 && gapAfter > gapBefore;

    const budgetValid = !budgetTouched || (budgetFinite && !budgetBelowZero && !belowCommitted && !limitExceedsBudget);
    const limitValid = !limitTouched || (limitFinite && !limitBelowZero && !limitExceedsBudget);

    const budgetHelperText = !budgetTouched
        ? 'Positive increases, negative decreases'
        : !budgetFinite
            ? 'Enter a valid number'
            : budgetBelowZero
                ? `Can't go below ${formatRupees(0)}`
                : belowCommitted
                    ? `Can't go below ${formatRupees(alreadyCommitted)} already committed`
                    : limitExceedsBudget
                        ? `Limit (${formatRupees(newLimit)}) would exceed this — raise Budget or lower Limit too`
                        : `New total: ${formatRupees(newBudget)}`;

    const limitHelperText = !limitTouched
        ? 'Positive increases, negative decreases'
        : !limitFinite
            ? 'Enter a valid number'
            : limitBelowZero
                ? `Can't go below ${formatRupees(0)}`
                : limitExceedsBudget
                    ? `Can't exceed Budget (${formatRupees(newBudget)})`
                    : `New total: ${formatRupees(newLimit)}`;

    const trimmedName = name.trim();
    const nameChanged = trimmedName !== (campaign?.name || '');
    const nameValid = trimmedName.length > 0;

    const isValid = (budgetTouched || limitTouched || nameChanged) && budgetValid && limitValid && nameValid;

    const handleSave = async () => {
        if (!isValid || !campaign) return;
        const campaignId = campaign._id || campaign.id;
        setSaving(true);
        try {
            const updated = await paymentportalservice.AdjustCampaignBudgetLimit({
                campaignId,
                addBudget: budgetDelta ?? undefined,
                addLimit: limitDelta ?? undefined,
                name: nameChanged ? trimmedName : undefined,
            });
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Campaign Updated!',
                showConfirmButton: false,
                timer: 1200,
            });
            onSaved?.(updated);
            onClose?.();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Could not update campaign',
                text: error?.response?.data || error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Edit sx={{ color: '#0EA5E9' }} />
                    <Typography variant="h6">Edit Campaign</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                    <TextField
                        label="Campaign Name"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={!nameValid}
                        helperText={!nameValid ? 'Campaign name cannot be empty' : ' '}
                    />
                    <TextField
                        label={`Adjust Budget (Current: ${formatRupees(currentBudget)})`}
                        type="number"
                        fullWidth
                        value={addBudget}
                        onChange={(e) => setAddBudget(e.target.value)}
                        error={!budgetValid}
                        helperText={budgetHelperText}
                        placeholder="e.g. 5000 or -5000"
                    />
                    <TextField
                        label={`Adjust Limit (Current: ${formatRupees(currentLimit)})`}
                        type="number"
                        fullWidth
                        value={addLimit}
                        onChange={(e) => setAddLimit(e.target.value)}
                        error={!limitValid}
                        helperText={limitHelperText}
                        placeholder="e.g. 5000 or -5000"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#ee4c3a' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isValid || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default EditBudgetLimitDialog;
